import React, { useEffect, useState, useRef, useCallback } from 'react'
import AdBanner from '../Common/AdBanner'
import {
  AlertCircle,
  Eye,
  Share,
  Newspaper,
  ArrowLeft,
  User,
  MessageSquare,
  ExternalLink,
  MoreHorizontal,
  Edit2,
  Trash2,
  ShieldAlert,
  Loader2,
  Link as LinkIcon,
  Heart,
} from 'lucide-react'
import { NewsItem, CommentWithUser } from '../../types'
import {
  incrementNewsView,
  getNewsViewCount,
  fetchSupabaseNewsItem,
  fetchSupabaseNewsList,
  getNewsLikes,
} from '../../services/footballService'
import { deleteNewsPermanently } from '../../services/adminService'
import { supabase } from '../../services/authService'
import { pointService } from '../../services/pointService'
import {
  fetchNewsComments,
  createNewsComment,
  deleteNewsComment,
  reportNewsComment,
  likeNewsComment,
  unlikeNewsComment,
  toggleNewsLike,
} from '../../services/newsCommentService'
import { FullPageLoader } from '../Common/FullPageLoader'
import { ConfirmModal } from '../Common/ConfirmModal'
import { ReportModal } from '../Common/ReportModal'
import { NewsDetailHeader } from '../News/NewsDetailHeader'
import { NewsCommentSection } from '../News/NewsCommentSection'
import { RelatedNews } from '../News/RelatedNews'
import { shareContent } from '../../services/shareService'
import { KeyBriefing } from '../News/KeyBriefing'
import { useLanguage } from '../../context/LanguageContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { t } from '../../i18n'
import { XlevenLogo } from '../Common/XlevenLogo'
import { WeeklyMagazineLogo } from '../Common/WeeklyMagazineLogo'
import { WebtoonLogo } from '../Common/WebtoonLogo'
import { clearCache } from '../../services/storageService'
import SEO from '../Common/SEO'

interface NewsDetailViewProps {
  newsId: string
  onBack: () => void
}

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1522778119026-d647f0565c6a?auto=format&fit=crop&w=1200&q=80'

const isValidUuid = (uuid: string) => {
  const re =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return re.test(uuid)
}

export const NewsDetailView: React.FC<NewsDetailViewProps> = ({
  newsId,
  onBack,
}) => {
  const { lang } = useLanguage()
  const { user } = useAuth()
  const { showToast } = useToast()

  const [newsItem, setNewsItem] = useState<NewsItem | null>(null)
  const [title, setTitle] = useState<string>('')
  const [summary, setSummary] = useState<string>('')
  const [content, setContent] = useState<string>('')
  const [predefinedAudioUrl, setPredefinedAudioUrl] = useState<string | null>(
    null,
  )
  const [loading, setLoading] = useState(true)
  const [viewCount, setViewCount] = useState<number>(0)
  const [likesCount, setLikesCount] = useState<number>(0)
  const [isLiked, setIsLiked] = useState(false)
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([])
  const [error, setError] = useState<string | null>(null)

  const [comments, setComments] = useState<
    (CommentWithUser & { is_liked?: boolean })[]
  >([])
  const [newComment, setNewComment] = useState('')
  const [isPosting, setIsPosting] = useState(false)

  const [hasActivatedAudio, setHasActivatedAudio] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const [isLoadingAudio, setIsLoadingAudio] = useState(false)
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [activeReportId, setActiveReportId] = useState<string | number | null>(
    null,
  )

  const [postMenuOpen, setPostMenuOpen] = useState(false)
  const [deletePostModalOpen, setDeletePostModalOpen] = useState(false)
  const [isDeletingPost, setIsDeletingPost] = useState(false)

  const [deleteCommentModalOpen, setDeleteCommentModalOpen] = useState(false)
  const [commentToDeleteId, setCommentToDeleteId] = useState<
    number | string | null
  >(null)
  const [isDeletingComment, setIsDeletingComment] = useState(false)

  const [isReportingPost, setIsReportingPost] = useState(false)
  const [isReportingComment, setIsReportingComment] = useState(false)
  const [reportPostModalOpen, setReportPostModalOpen] = useState(false)

  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null)
  const startTimeRef = useRef<number>(0)
  const offsetRef = useRef<number>(0)
  const timerIntervalRef = useRef<number | null>(null)
  const postMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        postMenuRef.current &&
        !postMenuRef.current.contains(e.target as Node)
      ) {
        setPostMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const stopAudio = useCallback(() => {
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop()
      } catch (e) {}
      sourceNodeRef.current = null
    }
    if (timerIntervalRef.current) {
      window.clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }
    setIsReading(false)
  }, [])

  const playFromOffset = useCallback(
    (timeOffset: number) => {
      if (!audioBuffer) return
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (
          window.AudioContext || (window as any).webkitAudioContext
        )()
      }
      stopAudio()
      const source = audioCtxRef.current.createBufferSource()
      source.buffer = audioBuffer
      source.playbackRate.value = 1.1
      source.connect(audioCtxRef.current.destination)
      const actualOffset = Math.max(
        0,
        Math.min(timeOffset, audioBuffer.duration),
      )
      source.start(0, actualOffset)
      startTimeRef.current = audioCtxRef.current.currentTime
      offsetRef.current = actualOffset
      sourceNodeRef.current = source
      setIsReading(true)
      timerIntervalRef.current = window.setInterval(() => {
        if (audioCtxRef.current) {
          const realElapsed =
            audioCtxRef.current.currentTime - startTimeRef.current
          const newTime = offsetRef.current + realElapsed * 1.1
          setCurrentTime(newTime)
          if (newTime >= audioBuffer.duration) {
            stopAudio()
            setCurrentTime(0)
            offsetRef.current = 0
          }
        }
      }, 100)
    },
    [audioBuffer, stopAudio],
  )

  const handleToggleAudio = async () => {
    if (!user) {
      onLoginPrompt()
      return
    }
    if (isLoadingAudio || !predefinedAudioUrl) return

    setHasActivatedAudio(true)
    if (audioBuffer) {
      if (isReading) stopAudio()
      else playFromOffset(currentTime)
      return
    }

    setIsLoadingAudio(true)
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (
          window.AudioContext || (window as any).webkitAudioContext
        )()
      }
      if (audioCtxRef.current.state === 'suspended')
        await audioCtxRef.current.resume()

      const response = await fetch(predefinedAudioUrl)
      if (!response.ok)
        throw new Error('Audio resource not found or inaccessible')

      const arrayBuffer = await response.arrayBuffer()
      const decodedBuffer =
        await audioCtxRef.current.decodeAudioData(arrayBuffer)
      setAudioBuffer(decodedBuffer)
      setDuration(decodedBuffer.duration)
      setIsLoadingAudio(false)
      playFromOffset(0)
    } catch (e: any) {
      setIsLoadingAudio(false)
      setHasActivatedAudio(false)
      showToast('error', t(lang, 'mergeFailed'))
    }
  }

  const handleSeek = (seekTime: number) => {
    if (!audioBuffer || duration <= 0) return
    setCurrentTime(seekTime)
    offsetRef.current = seekTime
    if (isReading) playFromOffset(seekTime)
  }

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await incrementNewsView(newsId, lang, user?.id || null)

      // newsId가 숫자 형태(Football API ID)일 경우 comments와 likes 조회를 스킵하여 400 에러 방지
    const isInternalNews = isValidUuid(newsId);

    const [itemData, listData, count, commentsData, likesData] = await Promise.all([
      fetchSupabaseNewsItem(newsId, lang),
      fetchSupabaseNewsList(lang, 0, 10),
      isInternalNews ? getNewsViewCount(newsId) : Promise.resolve(0),
      isInternalNews ? fetchNewsComments(newsId, lang) : Promise.resolve([]),
      isInternalNews ? getNewsLikes(newsId) : Promise.resolve([]),
    ])

      if (itemData) {
        setNewsItem(itemData)
        setTitle(itemData.title || '')
        setSummary(itemData.summary || '')
        setContent(itemData.content || '')
        setPredefinedAudioUrl(itemData.audio_url || null)
        setViewCount(count)
        setLikesCount(likesData.length)
        if (user) {
          setIsLiked(likesData.some((l) => l.user_id === user.id))
        }
        setComments(commentsData)
        setRelatedNews(listData.news.filter((n) => n.id !== newsId).slice(0, 4))
      } else {
        setError(t(lang, 'articleNotFound'))
      }
    } catch (e) {
      setError(t(lang, 'articleNotFound'))
    } finally {
      setLoading(false)
    }
  }, [newsId, lang, user?.id])

  useEffect(() => {
    loadData()
    // 페이지 진입 시 스크롤 상단 이동
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return () => stopAudio()
  }, [loadData, stopAudio])

  const handlePostComment = async (parentId?: number | null) => {
    if (!user) {
      onLoginPrompt()
      return
    }
    if (!newComment.trim()) return
    setIsPosting(true)
    try {
      const added = await createNewsComment({
        newsId,
        lang,
        content: newComment,
        parentId,
      })

      // 포인트 적립: 댓글 등록 시 5 포인트 (일 최대 100)
      const pointResult = await pointService.awardPointsWithLimit(
        user.id,
        5,
        t(lang, 'reasonCommentPost'),
        100,
      )
      if (pointResult.success && pointResult.pointsEarned > 0) {
        showToast(
          'success',
          `${t(lang, 'commentPosted')} +${pointResult.pointsEarned}P`,
        )
      } else {
        showToast('success', t(lang, 'postSuccess'))
      }

      setComments((prev) => [...prev, added])
      setNewComment('')
    } catch (e: any) {
      showToast('error', e.message)
    } finally {
      setIsPosting(false)
    }
  }

  const handleCommentDeleteClick = (id: string | number) => {
    setCommentToDeleteId(id)
    setDeleteCommentModalOpen(true)
  }

  const handleCommentDeleteConfirm = async () => {
    if (commentToDeleteId === null) return
    setIsDeletingComment(true)
    try {
      // 삭제 전 댓글 정보 찾기 (포인트 차감 대상 확인용)
      const targetComment = comments.find((c) => c.id === commentToDeleteId)

      await deleteNewsComment(commentToDeleteId)

      // 포인트 차감: 댓글 삭제 시 5 포인트 차감
      if (targetComment && targetComment.user_id) {
        await pointService.deductPoints(
          targetComment.user_id,
          5,
          t(lang, 'reasonCommentDelete'),
        )
        showToast('info', t(lang, 'pointDeducted'))
      }

      setComments((prev) => prev.filter((c) => c.id !== commentToDeleteId))
      setDeleteCommentModalOpen(false)
      setCommentToDeleteId(null)
    } catch (e: any) {
      showToast('error', t(lang, 'deleteFailed'))
    } finally {
      setIsDeletingComment(false)
    }
  }

  const handleNewsCommentLikeToggle = async (
    commentId: string | number,
    isLiked: boolean,
  ) => {
    if (!user) {
      onLoginPrompt()
      return
    }
    setComments((prev) =>
      prev.map((c) => {
        if (c.id === commentId) {
          const currentLikes = Number(c.likes_count) || 0
          return {
            ...c,
            is_liked: !isLiked,
            likes_count: isLiked
              ? Math.max(0, currentLikes - 1)
              : currentLikes + 1,
          }
        }
        return c
      }),
    )
    try {
      if (isLiked) await unlikeNewsComment(commentId)
      else await likeNewsComment(commentId)
    } catch (e) {
      const freshComments = await fetchNewsComments(newsId, lang)
      setComments(freshComments)
    }
  }

  const onLoginPrompt = () => {
    sessionStorage.setItem('redirectAfterLogin', window.getCurrentPath())
    setLoginModalOpen(true)
  }

  const handleShare = async () => {
    if (!newsItem) return
    const src = newsItem.source?.toLowerCase() || ''
    const isMagazine = src === 'magazine' || src === 'openai'
    const isWebtoon = src === 'webtoon'
    const isUserPost = !!newsItem.user_id
    const isPartner =
      src.includes('bbc') ||
      src.includes('teamtalk') ||
      src.includes('si') ||
      src.includes('sports illustrated')

    let prefix: string
    if (isMagazine) prefix = '[XLEVEN Magazine]'
    else if (isWebtoon) prefix = '[XLEVEN Webtoon]'
    else if (isUserPost && newsItem.author?.name) prefix = `[${newsItem.author.name}]`
    else if (!isPartner && newsItem.sourceName) prefix = `[${newsItem.sourceName}]`
    else prefix = '[XLEVEN]'

    const displaySummary = newsItem.summary
      ? newsItem.summary.substring(0, 120)
      : ''

    await shareContent(
      {
        title: `${prefix} ${newsItem.title}`,
        text: displaySummary,
        url: window.location.href,
      },
      lang,
      showToast,
    )
  }

  const handleDeletePost = async () => {
    if (!newsItem) return
    setIsDeletingPost(true)
    try {
      await deleteNewsPermanently(newsItem.id)

      // 포인트 차감: 피드 삭제 시 10 포인트 차감
      if (newsItem.user_id) {
        await pointService.deductPoints(
          newsItem.user_id,
          10,
          t(lang, 'reasonNewsDelete'),
        )
        showToast('info', t(lang, 'pointDeducted'))
      }

      clearCache()
      showToast('success', t(lang, 'postDeletedSuccess'))
      onBack()
    } catch (e: any) {
      showToast('error', t(lang, 'deleteFailed') + e.message)
    } finally {
      setIsDeletingPost(false)
      setDeletePostModalOpen(false)
    }
  }

  const handleReportPost = async (reason: string) => {
    if (!newsItem || !user) return

    if (!isValidUuid(newsItem.id)) {
      setReportPostModalOpen(false)
      showToast('info', t(lang, 'externalSourceReportError'))
      return
    }

    setIsReportingPost(true)
    try {
      const { error } = await supabase.from('news_reports').insert({
        news_id: newsItem.id,
        reporter_id: user.id,
        reason: reason,
      })

      setReportPostModalOpen(false)
      if (error) {
        if (error.code === '23505') {
          showToast('info', t(lang, 'alreadyReported'))
        } else {
          showToast('error', t(lang, 'syncFailedTryAgain'))
        }
      } else {
        await supabase
          .from('news_list')
          .update({ is_reported: true })
          .eq('id', newsItem.id)
        showToast('success', t(lang, 'reportCompleted'))
      }
    } catch (e: any) {
      setReportPostModalOpen(false)
      showToast('error', t(lang, 'syncFailedTryAgain'))
    } finally {
      setIsReportingPost(false)
    }
  }

  const handleReportCommentConfirm = async (reason: string) => {
    if (!activeReportId || !user) return
    setIsReportingComment(true)
    try {
      await reportNewsComment(activeReportId, reason)
      setReportModalOpen(false)
      setActiveReportId(null)
      showToast('success', t(lang, 'reportCompleted'))
    } catch (e: any) {
      setReportModalOpen(false)
      setActiveReportId(null)
      if (e.code === '23505') showToast('info', t(lang, 'alreadyReported'))
      else showToast('error', t(lang, 'syncFailedTryAgain'))
    } finally {
      setIsReportingComment(false)
    }
  }

  const handleEditPost = () => {
    if (newsItem) window.navigateTo(`/feed/edit/${newsItem.id}`)
  }

  const handleLikeToggle = async () => {
    if (!user) {
      onLoginPrompt()
      return
    }

    // Optimistic update
    const newIsLiked = !isLiked
    setIsLiked(newIsLiked)
    setLikesCount((prev) => prev + (newIsLiked ? 1 : -1))

    try {
      await toggleNewsLike(newsId)
    } catch (e) {
      // Revert on error
      setIsLiked(!newIsLiked)
      setLikesCount((prev) => prev + (!newIsLiked ? 1 : -1))
      showToast('error', t(lang, 'syncFailedTryAgain'))
    }
  }

  if (loading)
    return (
      <FullPageLoader
        isLoading={true}
        icon={Newspaper}
        text={t(lang, 'loadingIntelligence')}
      />
    )
  if (error || !newsItem)
    return (
      <div className='min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center'>
        <AlertCircle
          size={48}
          className='text-rose-500 mb-4'
        />
        <h2 className='text-2xl font-black text-slate-900 mb-2'>
          {error || t(lang, 'articleNotFound')}
        </h2>
        <button
          onClick={onBack}
          className='mt-6 flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95'
        >
          <ArrowLeft size={16} /> {t(lang, 'goBack')}
        </button>
      </div>
    )

  const sourceLower = newsItem.source?.toLowerCase() || ''
  const isPartnerSource =
    sourceLower.includes('bbc') ||
    sourceLower.includes('teamtalk') ||
    sourceLower.includes('si') ||
    sourceLower.includes('sports illustrated')
  const isWeeklyMagazine = sourceLower === 'magazine' || sourceLower === 'openai'
  const isWeeklyWebtoon = sourceLower === 'webtoon'
  const isUserPost = !!newsItem.user_id
  const isMine = user && user.id === newsItem.user_id
  const displaySourceName = isPartnerSource
    ? 'XLEVEN'
    : isUserPost
      ? newsItem.author?.name
      : newsItem.source || 'XLEVEN INTEL'

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": title || newsItem.title,
    "description": (summary || newsItem.summary) || (content || newsItem.content)?.substring(0, 160),
    "image": [newsItem.image || FALLBACK_IMAGE],
    "datePublished": newsItem.publishedAt || new Date().toISOString(),
    "dateModified": newsItem.publishedAt || new Date().toISOString(),
    "author": [{
      "@type": "Organization",
      "name": "XLEVEN",
      "url": "https://www.xleven.co"
    }],
    "publisher": {
      "@type": "Organization",
      "name": "XLEVEN",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dvcrglecwqmqcrioedln.supabase.co/storage/v1/object/public/xleven/thum.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  return (
    <div className={`${isWeeklyWebtoon ? 'bg-slate-50/50' : 'bg-white'} min-h-screen animate-in fade-in duration-300`}>
      <SEO
        title={title || newsItem.title}
        description={((summary || newsItem.summary) || (content || newsItem.content))?.substring(0, 160)}
        jsonLd={jsonLd}
      />

      <ConfirmModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onConfirm={() => window.navigateTo('/login')}
        title={t(lang, 'loginRequiredTitle')}
        confirmLabel={t(lang, 'signInLabel')}
      />
      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => {
          if (!isReportingComment) {
            setReportModalOpen(false)
            setActiveReportId(null)
          }
        }}
        onConfirm={handleReportCommentConfirm}
        isLoading={isReportingComment}
        lang={lang}
      />
      <ConfirmModal
        isOpen={deletePostModalOpen}
        onClose={() => setDeletePostModalOpen(false)}
        onConfirm={handleDeletePost}
        isLoading={isDeletingPost}
        title={t(lang, 'deleteFeed')}
        description={t(lang, 'deleteFeedConfirm')}
        confirmLabel={t(lang, 'delete')}
      />
      <ConfirmModal
        isOpen={deleteCommentModalOpen}
        onClose={() => setDeleteCommentModalOpen(false)}
        onConfirm={handleCommentDeleteConfirm}
        isLoading={isDeletingComment}
        title={t(lang, 'deleteCommentTitle')}
        confirmLabel={t(lang, 'delete')}
      />
      <ReportModal
        isOpen={reportPostModalOpen}
        onClose={() => {
          if (!isReportingPost) setReportPostModalOpen(false)
        }}
        onConfirm={handleReportPost}
        isLoading={isReportingPost}
        lang={lang}
        title={t(lang, 'reportFeedTitle')}
      />

      <NewsDetailHeader
        newsItem={newsItem}
        onBack={onBack}
        onShare={handleShare}
        lang={lang}
        audioProps={{
          url: predefinedAudioUrl,
          isLoading: isLoadingAudio,
          isReading,
          hasActivated: hasActivatedAudio,
          currentTime,
          duration,
          onToggle: handleToggleAudio,
          onSeek: handleSeek,
          onClose: () => {
            stopAudio()
            setHasActivatedAudio(false)
          },
        }}
      />

      <div className='max-w-[700px] mx-auto border-x border-gray-50 min-h-screen'>
        <article className='p-4 md:p-6 pb-20'>
          <div className={`flex items-center justify-between mb-8 z-20 ${isWeeklyWebtoon ? 'bg-slate-50/90' : 'bg-white/95'} backdrop-blur-md py-4 -mx-4 px-4 border-b border-gray-100/50 transition-all duration-300`}>
            <div className='flex items-center gap-3'>
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border ${isWeeklyWebtoon ? 'border-[#00d564]/20' : 'border-gray-100'} shadow-sm bg-gray-50`}
              >
                {isPartnerSource ? (
                  <XlevenLogo
                    theme='light'
                    hideText={true}
                  />
                ) : isWeeklyMagazine ? (
                  <WeeklyMagazineLogo size={46} />
                ) : isWeeklyWebtoon ? (
                  <WebtoonLogo size={46} />
                ) :   isUserPost && newsItem.author?.avatar ? (
                  <img
                    src={newsItem.author.avatar}
                    className='w-full h-full object-cover'
                    alt=''
                  />
                ) : newsItem.sourceIcon ? (
                  <img
                    src={newsItem.sourceIcon}
                    className='w-full h-full object-cover'
                    alt=''
                  />
                ) : (
                  <span className='text-xs font-black text-slate-300'>XL</span>
                )}
              </div>
                <div className='flex flex-col'>
                  <span className={`font-black ${isWeeklyWebtoon ? 'text-[#00d564]' : 'text-slate-900'} text-base leading-none uppercase tracking-tight`}>
                    {isWeeklyMagazine ? 'Magazine' : isWeeklyWebtoon ? 'Webtoon' : displaySourceName}
                  </span>
                  <div className='flex items-center gap-1.5 mt-1.5'>
                    <span className='text-slate-500 text-[10px] font-bold uppercase'>
                      @
                      {isPartnerSource
                        ? 'xleven_official'
                        : newsItem.source?.toLowerCase().replace(/\s+/g, '') ||
                          'scout'}
                    </span>
                    <span className='text-slate-300 text-[10px]'>·</span>
                    <span className={`${isWeeklyWebtoon ? 'text-slate-400' : 'text-rose-500'} text-[10px] font-black uppercase tracking-tighter`}>
                      {newsItem.time}
                    </span>
                  </div>
                </div>
            </div>
            <div
              className='relative'
              ref={postMenuRef}
            >
              <button
                onClick={() => setPostMenuOpen(!postMenuOpen)}
                className='w-10 h-10 flex items-center justify-center rounded-full md:hover:bg-gray-100 text-slate-400 transition-all active:scale-90'
              >
                <MoreHorizontal size={20} />
              </button>
              {postMenuOpen && (
                <div className='absolute top-full right-0 mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200'>
                  <div className='p-1.5 flex flex-col gap-0.5'>
                    <button
                      onClick={() => {
                        setPostMenuOpen(false)
                        if (!user) onLoginPrompt()
                        else setReportPostModalOpen(true)
                      }}
                      className='flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 md:hover:bg-slate-50'
                    >
                      <ShieldAlert
                        size={16}
                        className='text-rose-500'
                      />{' '}
                      <span>{t(lang, 'reportFeed')}</span>
                    </button>
                    {(isMine || user?.role === 'admin') && (
                      <>
                        <button
                          onClick={() => {
                            setPostMenuOpen(false)
                            handleEditPost()
                          }}
                          className='flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 md:hover:bg-slate-50'
                        >
                          <Edit2
                            size={16}
                            className='text-indigo-500'
                          />{' '}
                          <span>{t(lang, 'editFeed')}</span>
                        </button>
                        <button
                          onClick={() => {
                            setPostMenuOpen(false)
                            setDeletePostModalOpen(true)
                          }}
                          className='flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-rose-500 md:hover:bg-rose-50 transition-all shadow-sm'
                        >
                          <Trash2 size={16} />{' '}
                          <span>{t(lang, 'deleteFeed')}</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {newsItem.image && (newsItem.source === 'magazine' || newsItem.source === 'webtoon') && (
            <div className={`relative ${isWeeklyWebtoon ? 'mb-8 mt-4' : 'mt-8 mb-12'} rounded-2xl md:rounded-[32px] overflow-hidden border ${isWeeklyWebtoon ? 'border-slate-200 shadow-xl' : 'border-gray-100 shadow-sm'} aspect-[2/3] bg-gray-50 group`}>
              <img
                src={newsItem.image}
                alt=''
                className='w-full h-full object-cover'
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_IMAGE
                }}
              />
              {(isPartnerSource || isWeeklyMagazine) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(newsItem.url, '_blank')
                  }}
                  className='absolute bottom-4 right-4 bg-black/60 backdrop-blur-md px-4 py-2 rounded-2xl text-[10px] font-black text-white uppercase flex items-center gap-2 md:hover:bg-rose-500 transition-all border border-white/10 shadow-lg'
                >
                  Source: {newsItem.source === 'magazine' || newsItem.source === 'webtoon' ? 'openai' : newsItem.source || 'INTEL'}{' '}
                  <ExternalLink
                    size={12}
                    strokeWidth={3}
                  />
                </button>
              )}
            </div>
          )}

          {(newsItem.summary || summary) && !isWeeklyMagazine && (
            <div className={isWeeklyWebtoon ? 'mb-10 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm' : 'hidden'}>
              {isWeeklyWebtoon && <h3 className="text-[10px] font-black text-[#00d564] uppercase tracking-widest mb-3">Synopsis</h3>}
              <KeyBriefing content={summary || newsItem.summary || ''} isWebtoon={isWeeklyWebtoon} />
            </div>
          )}
          <div className={`prose prose-slate max-w-none ${isWeeklyWebtoon ? 'mb-16' : 'mb-10'}`}>
            <div 
              className='text-slate-600 text-base md:text-lg leading-relaxed whitespace-pre-wrap font-medium'
              itemProp="articleBody"
            >
              {content || (
                <div className="py-20 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                  <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">
                    {t(lang, 'loadingIntelligence')}
                  </p>
                </div>
              )}
              {content && content.length < 200 && (
                <div className="mt-8 p-6 bg-rose-50 rounded-2xl border border-rose-100">
                  <p className="text-rose-900 text-xs font-bold leading-relaxed">
                    XLEVEN Intel: 본 콘텐츠는 실시간 축구 데이터를 기반으로 AI가 분석한 최신 정보입니다. 
                    더 자세한 경기 스탯과 선수 정보는 하단의 관련 뉴스와 경기 탭에서 확인하실 수 있습니다.
                  </p>
                </div>
              )}
            </div>
          </div>

          <AdBanner slotId="7080513270" className="my-6" />

          <div className='py-6 border-y border-gray-50 flex items-center justify-between text-slate-400 mb-0'>
            <div className='flex items-center gap-8'>
              <button 
                onClick={handleLikeToggle}
                className={`flex items-center gap-1.5 transition-colors active:scale-90 ${isLiked ? 'text-rose-500' : 'md:hover:text-rose-500'}`}
              >
                <Heart size={18} className={isLiked ? 'fill-rose-500' : ''} />
                <span className='text-[11px] font-black tabular-nums'>
                  {likesCount.toLocaleString()}
                </span>
              </button>
              <div className='flex items-center gap-1.5'>
                <MessageSquare size={17} />
                <span className='text-[11px] font-black tabular-nums'>
                  {comments.length}
                </span>
              </div>
              <div className='flex items-center gap-1.5'>
                <Eye size={18} />
                <span className='text-[11px] font-black tabular-nums'>
                  {viewCount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <RelatedNews
            news={relatedNews}
            lang={lang}
            onNewsClick={(id) => window.navigateTo(`/feed/item/${id}`)}
          />

          <NewsCommentSection
            comments={comments}
            lang={lang}
            user={user}
            isPosting={isPosting}
            newComment={newComment}
            onCommentChange={setNewComment}
            onPost={handlePostComment}
            onDelete={handleCommentDeleteClick}
            onLikeToggle={handleNewsCommentLikeToggle}
            onReport={(id) => {
              if (!user) onLoginPrompt()
              else {
                setActiveReportId(id)
                setReportModalOpen(true)
              }
            }}
            onLoginPrompt={onLoginPrompt}
          />
        </article>
      </div>
    </div>
  )
}
